import { useEffect, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useUserPermissions } from '../../../../apis/user-details';
import { useAllRestaurants } from '../../../../apis/shops-api';
import useRestaurantStore from '../../../../stores/restaurantStore';

// A restaurant's own pages. The bare `/:restaurant` landing route is matched separately, since the same
// single-segment pattern also matches every static page (/profile, /orders, /products, …).
const RESTAURANT_SUB_PAGES = ['/:restaurant/home', '/:restaurant/out-of-stock', '/:restaurant/orders'];

const matchShopInPath = (pathname: string): { shopId: string; isSubPage: boolean } | undefined => {
  for (const path of RESTAURANT_SUB_PAGES) {
    const shopId = matchPath(path, pathname)?.params.restaurant;
    if (shopId) return { shopId, isSubPage: true };
  }

  const shopId = matchPath('/:restaurant', pathname)?.params.restaurant;
  return shopId ? { shopId, isSubPage: false } : undefined;
};

/** Whether the page is one of a restaurant's own sub-pages, i.e. one the drawer's restaurant tab links to. */
export const isRestaurantSubPage = (pathname: string): boolean => !!matchShopInPath(pathname)?.isSubPage;

// The restaurants list (MyRestaurantsPage), reachable at both paths: `/home` is where GuardedRoute sends a user
// who may not open a page. matchPath, so `/home/` counts too, as it does for the router.
export const isOverviewPage = (pathname: string): boolean =>
  !!matchPath('/', pathname) || !!matchPath('/home', pathname);

/**
 * The shop the drawer's restaurant links point at: the page's shop, else the remembered one (it can belong to a
 * previous user), else the user's first shop — each only if the user may see it. Users with no shops (admins) may
 * see any. The result is stored, so it survives non-shop pages and reloads. `isLoading` is true until the
 * permissions are in, while `allowedShops` is still the empty list it will not stay.
 */
export const useSelectedShop = (): { selectedShopId?: string; allowedShops: string[]; isLoading: boolean } => {
  const { shops: allowedShops, isLoading } = useUserPermissions();
  const storedShopId = useRestaurantStore((state) => state.selectedShopExternalId);
  const selectRestaurant = useRestaurantStore((state) => state.selectRestaurant);
  const { pathname } = useLocation();
  // Cache-only: the drawer must not fetch every shop on every page just to recognise a bare `/:restaurant` URL.
  const { data: knownRestaurants } = useAllRestaurants(false, { enabled: false });

  const canSeeAnyShop = allowedShops.length === 0;

  const urlShopId = useMemo(() => {
    const match = matchShopInPath(pathname);
    if (!match) return undefined;

    if (!canSeeAnyShop) return allowedShops.includes(match.shopId) ? match.shopId : undefined;
    if (match.isSubPage) return match.shopId;
    return knownRestaurants?.some((r) => r.shopExternalId === match.shopId) ? match.shopId : undefined;
  }, [pathname, canSeeAnyShop, allowedShops, knownRestaurants]);

  const validStoredShopId =
    storedShopId && (canSeeAnyShop || allowedShops.includes(storedShopId)) ? storedShopId : undefined;

  const selectedShopId = urlShopId ?? validStoredShopId ?? allowedShops[0];

  useEffect(() => {
    if (!isLoading && selectedShopId && selectedShopId !== storedShopId) selectRestaurant(selectedShopId);
  }, [isLoading, selectedShopId, storedShopId, selectRestaurant]);

  return { selectedShopId, allowedShops, isLoading };
};
